/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useArsForm.ts (ARS - Route Selections)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing ARS profile form state, validation, and submission.
    Handles create, update, and duplicate operations.

────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createArsProfile, updateArsProfile } from '../api/arsApi';
import { FormState, RouteFormState } from '../types';
import { getDefaultFormState, createEmptyRoute, applySequentialPriorities } from '../utils/formDefaults';
import { validateForm } from '../utils/validators';
import {
  serializeFormState,
  cloneFormState,
  addRouteToFormState,
  removeRouteFromFormState,
  moveRouteInFormState,
  updateRouteInFormState
} from '../utils/routeHelpers';

/**
 * Hook for managing ARS form
 */
export function useArsForm(
  id: string | undefined,
  duplicateId: string | null,
  initialData: FormState | null
) {
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();

  const isEditMode = id !== 'new';
  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [originalState, setOriginalState] = useState<FormState>(defaultFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const initializedRef = useRef(false);
  const hasInitialRouteRef = useRef(false);
  const lastInitialDataRef = useRef<FormState | null>(null);

  // Initialize form data when initialData changes
  useEffect(() => {
    if (initialData) {
      const initialDataStr = JSON.stringify(initialData);
      const lastDataStr = lastInitialDataRef.current ? JSON.stringify(lastInitialDataRef.current) : null;

      if (initialDataStr !== lastDataStr) {
        lastInitialDataRef.current = initialData;
        setFormState(cloneFormState(initialData));
        setOriginalState(cloneFormState(duplicateId ? { ...initialData, routes: [] } : initialData));
        initializedRef.current = true;
        hasInitialRouteRef.current = true;
      }
    } else if (!isEditMode && !duplicateId) {
      // Reset refs when navigating to new form to ensure clean state
      if (initializedRef.current) {
        initializedRef.current = false;
        lastInitialDataRef.current = null;
      }
      // Only set default state if not already initialized
      if (!initializedRef.current) {
        const defaultState = getDefaultFormState();
        defaultState.routes = applySequentialPriorities([createEmptyRoute(1)]);
        setFormState(defaultState);
        setOriginalState(cloneFormState(defaultState));
        initializedRef.current = true;
        hasInitialRouteRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isEditMode, duplicateId, id]); // Added id to dependencies to detect navigation changes

  const handleChange = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const addRoute = useCallback(() => {
    setFormState((prev) => addRouteToFormState(prev));
  }, []);

  const removeRoute = useCallback((index: number) => {
    setFormState((prev) => removeRouteFromFormState(prev, index));
  }, []);

  const moveRoute = useCallback((index: number, direction: number) => {
    setFormState((prev) => moveRouteInFormState(prev, index, direction));
  }, []);

  const updateRoute = useCallback(<K extends keyof RouteFormState>(
    index: number,
    field: K,
    value: RouteFormState[K]
  ) => {
    setFormState((prev) => updateRouteInFormState(prev, index, field, value));
  }, []);

  const hasChanges = useMemo(() => {
    const includeIds = isEditMode;
    const current = JSON.stringify(serializeFormState(formState, includeIds));
    const baseline = JSON.stringify(serializeFormState(originalState, includeIds));
    return current !== baseline;
  }, [formState, originalState, isEditMode]);

  const handleSave = useCallback(async (): Promise<FormState & { id?: number; uuid?: string } | undefined> => {
      // hasChanges validation removed

    const validationErrors = validateForm(formState, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError(t('ars.messages.validationError'));
      return undefined;
    }

    setIsSaving(true);
    try {
      const payload = serializeFormState(formState, isEditMode);

      const result = isEditMode
        ? await updateArsProfile(id!, payload)
        : await createArsProfile(payload);

      const successMessage = isEditMode ? t('ars.messages.updateSuccess') : t('ars.messages.createSuccess');
      showSuccess(successMessage);
      setOriginalState(cloneFormState(formState));

      // Return data with ID for navigation
      return { ...formState, id: result?.id, uuid: result?.uuid } as FormState & { id?: number; uuid?: string };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('ars.messages.updateError');
      showError(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formState, hasChanges, id, isEditMode, showError, showInfo, showSuccess, t]);

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormState(cloneFormState(originalState));
      setErrors({});
      return;
    }

    const defaultState = getDefaultFormState();
    defaultState.routes = applySequentialPriorities([createEmptyRoute(1)]);
    setFormState(defaultState);
    setOriginalState(cloneFormState(defaultState));
    setErrors({});
    hasInitialRouteRef.current = false;
  }, [isEditMode, originalState]);

  return {
    formState,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    addRoute,
    removeRoute,
    moveRoute,
    updateRoute,
    handleSave,
    handleCancel
  };
}

